import { useEffect, useState } from "react";
import api, { API } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Clock, Download, Circle, FileXls, Warning } from "@phosphor-icons/react";
import { humanTime } from "@/lib/utils";
import { toast } from "sonner";

const fmtDur = (s) => {
  s = Number(s) || 0;
  const h = Math.floor(s / 3600), m = Math.floor((s % 3600) / 60);
  if (h) return `${h}h ${m}m`;
  if (m) return `${m}m`;
  return `${s}s`;
};

const ANOM_LABEL = { new_ip: "New IP", odd_hours: "Odd hours" };

export default function SessionActivity() {
  const [data, setData] = useState(null);
  const [day, setDay] = useState("");
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [anomOnly, setAnomOnly] = useState(false);

  useEffect(() => { api.get("/reports/sessions").then(r => setData(r.data)).catch(() => {}); }, []);

  if (!data) return <div className="p-6 text-muted-foreground">Loading…</div>;

  const daily = data.daily || [];
  const selectedDay = day || data.today;
  const todayRows = daily.filter(d => d.date === selectedDay);
  const rangeRows = daily.filter(d => (!from || d.date >= from) && (!to || d.date <= to));
  const rangeTotals = rangeRows.reduce((a, d) => ({
    sessions: a.sessions + d.sessions, online: a.online + d.online_seconds,
    active: a.active + d.active_seconds, idle: a.idle + d.idle_seconds,
  }), { sessions: 0, online: 0, active: 0, idle: 0 });
  const logRows = anomOnly ? data.sessions.filter(s => (s.anomalies || []).length) : data.sessions;

  const exportCsv = () => {
    const lines = [
      [`Daily summary${from || to ? ` (${from || "start"} to ${to || data.today})` : ""}; times in IST`],
      ["Date", "Name", "Email", "Role", "Sessions", "First login", "Last logout", "Online", "Active", "Idle"],
      ...rangeRows.map(d => [d.date, d.name, d.email, d.role, d.sessions, d.first_login, d.last_logout, fmtDur(d.online_seconds), fmtDur(d.active_seconds), fmtDur(d.idle_seconds)]),
      ["TOTAL", "", "", "", rangeTotals.sessions, "", "", fmtDur(rangeTotals.online), fmtDur(rangeTotals.active), fmtDur(rangeTotals.idle)],
      [""],
      ["Session log"],
      ["Name", "Email", "Role", "Login", "Logout", "IP", "Anomalies", "Online", "Active", "Idle", "Status"],
      ...data.sessions.map(s => [s.name, s.email, s.role, (s.login_at || "").slice(0, 19), (s.logout_at || "").slice(0, 19), s.ip, (s.anomalies || []).map(a => ANOM_LABEL[a] || a).join(" | "), fmtDur(s.online_seconds), fmtDur(s.active_seconds), fmtDur(s.idle_seconds), s.active ? "online" : (s.end_reason || "ended")]),
    ];
    const csv = lines.map(l => l.map(c => `"${String(c ?? "").replace(/"/g, '""')}"`).join(",")).join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const a = document.createElement("a"); a.href = URL.createObjectURL(blob); a.download = `session-activity-${new Date().toISOString().slice(0, 10)}.csv`; a.click();
    toast.success("CSV exported");
  };

  const exportExcel = async () => {
    try {
      const params = new URLSearchParams();
      if (from) params.set("date_from", from);
      if (to) params.set("date_to", to);
      const token = localStorage.getItem("helix_token");
      const res = await fetch(`${API}/reports/sessions/export?${params.toString()}`, { headers: { Authorization: `Bearer ${token}` } });
      if (!res.ok) throw new Error();
      const url = URL.createObjectURL(await res.blob());
      const a = document.createElement("a"); a.href = url; a.download = `session-daily-${from || "all"}_${to || data.today}.xlsx`;
      document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
      toast.success("Excel exported");
    } catch { toast.error("Excel export failed"); }
  };

  return (
    <div className="p-6" data-testid="sessions-view">
      <div className="flex items-end justify-between mb-6">
        <div>
          <div className="text-[11px] font-mono uppercase tracking-widest text-primary mb-1">Administration</div>
          <h1 className="font-display text-3xl sm:text-4xl font-black flex items-center gap-3"><Clock size={30} className="text-primary" weight="fill" /> Session Activity</h1>
          <p className="text-sm text-muted-foreground mt-2">Agent login/logout, online time, and active vs idle breakdown.</p>
        </div>
        <div className="flex items-center gap-2">
          <Button data-testid="sessions-export" onClick={exportCsv} variant="outline" className="rounded-sm font-mono text-xs uppercase tracking-widest"><Download size={14} className="mr-2" />CSV</Button>
          <Button data-testid="sessions-export-excel" onClick={exportExcel} variant="outline" className="rounded-sm font-mono text-xs uppercase tracking-widest border-emerald-500/40 text-emerald-700 dark:text-emerald-400 hover:bg-emerald-500/10"><FileXls size={14} className="mr-2" />Excel</Button>
        </div>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <Card className="p-6 border-border rounded-sm" data-testid="sessions-online-now">
          <div className="text-[11px] font-mono uppercase tracking-widest text-muted-foreground mb-2">Online Now</div>
          <div className="font-display text-4xl text-emerald-700 dark:text-emerald-400">{data.online_now}</div>
        </Card>
        <Card className="p-6 border-border rounded-sm">
          <div className="text-[11px] font-mono uppercase tracking-widest text-muted-foreground mb-2">Users Tracked</div>
          <div className="font-display text-4xl">{data.summary.length}</div>
        </Card>
        <Card className="p-6 border-border rounded-sm">
          <div className="text-[11px] font-mono uppercase tracking-widest text-muted-foreground mb-2">Total Sessions</div>
          <div className="font-display text-4xl">{data.sessions.length}</div>
        </Card>
        <Card className="p-6 border-border rounded-sm">
          <div className="text-[11px] font-mono uppercase tracking-widest text-muted-foreground mb-2">Total Active Time</div>
          <div className="font-display text-3xl text-primary">{fmtDur(data.summary.reduce((a, u) => a + u.active_seconds, 0))}</div>
        </Card>
      </div>

      <Card className="border-border rounded-sm overflow-hidden mb-6" data-testid="sessions-summary">
        <div className="p-4 border-b border-border flex flex-wrap items-center justify-between gap-2">
          <div className="font-display text-lg">Per-User Summary</div>
          <div className="flex items-center gap-2" data-testid="sessions-summary-day">
            <span className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground">Day (IST)</span>
            <Input type="date" value={selectedDay} max={data.today} onChange={(e) => setDay(e.target.value)} data-testid="sessions-day-picker" className="h-8 w-[150px] rounded-sm font-mono text-xs" />
          </div>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead><tr className="text-[10px] uppercase tracking-widest text-muted-foreground font-mono border-b border-border">
              <th className="text-left px-4 py-3">User</th><th className="text-left px-4 py-3">Role</th><th className="text-left px-4 py-3">Sessions</th><th className="text-left px-4 py-3">First Login</th><th className="text-left px-4 py-3">Last Logout</th><th className="text-left px-4 py-3">Online</th><th className="text-left px-4 py-3">Active</th><th className="text-left px-4 py-3">Idle</th><th className="text-left px-4 py-3">Status</th>
            </tr></thead>
            <tbody>{todayRows.map(u => (
              <tr key={u.email} className="border-b border-border/60 hover:bg-muted/20" data-testid={`sessions-summary-row-${u.email}`}>
                <td className="px-4 py-3"><div>{u.name}</div><div className="text-xs font-mono text-muted-foreground">{u.email}</div></td>
                <td className="px-4 py-3 font-mono text-xs uppercase">{u.role}</td>
                <td className="px-4 py-3 font-mono">{u.sessions}</td>
                <td className="px-4 py-3 font-mono text-xs">{u.first_login}</td>
                <td className="px-4 py-3 font-mono text-xs text-muted-foreground">{u.last_logout}</td>
                <td className="px-4 py-3 font-mono">{fmtDur(u.online_seconds)}</td>
                <td className="px-4 py-3 font-mono text-emerald-700 dark:text-emerald-400">{fmtDur(u.active_seconds)}</td>
                <td className="px-4 py-3 font-mono text-muted-foreground">{fmtDur(u.idle_seconds)}</td>
                <td className="px-4 py-3">{u.currently_online ? <span className="inline-flex items-center gap-1 text-xs text-emerald-700 dark:text-emerald-400"><Circle size={8} weight="fill" /> online</span> : <span className="text-xs text-muted-foreground">offline</span>}</td>
              </tr>
            ))}
            {todayRows.length === 0 && <tr><td colSpan={9} className="text-center py-8 text-muted-foreground text-sm">No sign-ins on {selectedDay} (IST).</td></tr>}
            </tbody>
          </table>
        </div>
      </Card>

      <Card className="border-border rounded-sm overflow-hidden mb-6" data-testid="sessions-daily">
        <div className="p-4 border-b border-border flex flex-wrap items-center justify-between gap-3">
          <div className="font-display text-lg">Daily Breakdown <span className="text-sm text-muted-foreground font-mono">({rangeRows.length})</span></div>
          <div className="flex items-center gap-2">
            <span className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground">From</span>
            <Input type="date" value={from} max={to || data.today} onChange={(e) => setFrom(e.target.value)} data-testid="sessions-from" className="h-8 w-[150px] rounded-sm font-mono text-xs" />
            <span className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground">To</span>
            <Input type="date" value={to} min={from} max={data.today} onChange={(e) => setTo(e.target.value)} data-testid="sessions-to" className="h-8 w-[150px] rounded-sm font-mono text-xs" />
            {(from || to) && <Button variant="ghost" size="sm" className="h-8 rounded-sm font-mono text-[10px] uppercase" onClick={() => { setFrom(""); setTo(""); }} data-testid="sessions-range-clear">Clear</Button>}
          </div>
        </div>
        <div className="overflow-x-auto max-h-[420px] overflow-y-auto">
          <table className="w-full text-sm">
            <thead className="sticky top-0 bg-card"><tr className="text-[10px] uppercase tracking-widest text-muted-foreground font-mono border-b border-border">
              <th className="text-left px-4 py-3">Date</th><th className="text-left px-4 py-3">User</th><th className="text-left px-4 py-3">Sessions</th><th className="text-left px-4 py-3">First Login</th><th className="text-left px-4 py-3">Last Logout</th><th className="text-left px-4 py-3">Online</th><th className="text-left px-4 py-3">Active</th><th className="text-left px-4 py-3">Idle</th>
            </tr></thead>
            <tbody>{rangeRows.map(d => (
              <tr key={`${d.email}-${d.date}`} className="border-b border-border/60 hover:bg-muted/20" data-testid={`sessions-daily-row-${d.email}-${d.date}`}>
                <td className="px-4 py-3 font-mono text-xs">{d.date}</td>
                <td className="px-4 py-3"><div className="text-xs">{d.name}</div><div className="text-[10px] font-mono text-muted-foreground">{d.email}</div></td>
                <td className="px-4 py-3 font-mono">{d.sessions}</td>
                <td className="px-4 py-3 font-mono text-xs">{d.first_login}</td>
                <td className="px-4 py-3 font-mono text-xs text-muted-foreground">{d.last_logout}</td>
                <td className="px-4 py-3 font-mono text-xs">{fmtDur(d.online_seconds)}</td>
                <td className="px-4 py-3 font-mono text-xs text-emerald-700 dark:text-emerald-400">{fmtDur(d.active_seconds)}</td>
                <td className="px-4 py-3 font-mono text-xs text-muted-foreground">{fmtDur(d.idle_seconds)}</td>
              </tr>
            ))}
            {rangeRows.length === 0 && <tr><td colSpan={8} className="text-center py-8 text-muted-foreground text-sm">No sessions in this range.</td></tr>}
            </tbody>
            {rangeRows.length > 0 && (
              <tfoot className="sticky bottom-0 bg-muted/40"><tr className="font-mono text-xs border-t border-border" data-testid="sessions-daily-total">
                <td className="px-4 py-3 font-semibold uppercase" colSpan={2}>Total</td>
                <td className="px-4 py-3 font-semibold">{rangeTotals.sessions}</td>
                <td className="px-4 py-3" colSpan={2}></td>
                <td className="px-4 py-3 font-semibold">{fmtDur(rangeTotals.online)}</td>
                <td className="px-4 py-3 font-semibold text-emerald-700 dark:text-emerald-400">{fmtDur(rangeTotals.active)}</td>
                <td className="px-4 py-3 font-semibold text-muted-foreground">{fmtDur(rangeTotals.idle)}</td>
              </tr></tfoot>
            )}
          </table>
        </div>
      </Card>

      <Card className="border-border rounded-sm overflow-hidden" data-testid="sessions-log">
        <div className="p-4 border-b border-border flex flex-wrap items-center justify-between gap-3">
          <div className="font-display text-lg">Session Log <span className="text-sm text-muted-foreground font-mono">({logRows.length})</span></div>
          <label className="flex items-center gap-2 cursor-pointer">
            <Switch checked={anomOnly} onCheckedChange={setAnomOnly} data-testid="sessions-anom-toggle" />
            <span className="text-xs font-mono uppercase tracking-wide text-muted-foreground flex items-center gap-1"><Warning size={13} className="text-amber-600 dark:text-amber-400" /> Anomalies only</span>
          </label>
        </div>
        <div className="overflow-x-auto max-h-[500px] overflow-y-auto">
          <table className="w-full text-sm">
            <thead className="sticky top-0 bg-card"><tr className="text-[10px] uppercase tracking-widest text-muted-foreground font-mono border-b border-border">
              <th className="text-left px-4 py-3">User</th><th className="text-left px-4 py-3">Login</th><th className="text-left px-4 py-3">Logout</th><th className="text-left px-4 py-3">IP</th><th className="text-left px-4 py-3">Flags</th><th className="text-left px-4 py-3">Online</th><th className="text-left px-4 py-3">Active/Idle</th><th className="text-left px-4 py-3">Status</th>
            </tr></thead>
            <tbody>{logRows.map(s => (
              <tr key={s.id} className={`border-b border-border/60 hover:bg-muted/20 ${(s.anomalies || []).length ? "bg-amber-500/[0.04]" : ""}`} data-testid={`sessions-log-row-${s.id}`}>
                <td className="px-4 py-3"><div className="text-xs">{s.name}</div><div className="text-[10px] font-mono text-muted-foreground">{s.role}</div></td>
                <td className="px-4 py-3 font-mono text-xs">{humanTime(s.login_at)}</td>
                <td className="px-4 py-3 font-mono text-xs text-muted-foreground">{s.logout_at ? humanTime(s.logout_at) : "—"}</td>
                <td className="px-4 py-3 font-mono text-xs text-muted-foreground">{s.ip || "—"}</td>
                <td className="px-4 py-3">
                  <div className="flex flex-wrap gap-1">
                    {(s.anomalies || []).map(a => (
                      <span key={a} data-testid={`sessions-anom-${a}-${s.id}`} className="inline-flex items-center gap-1 text-[9px] font-mono uppercase px-1.5 py-0.5 border border-amber-500/50 text-amber-700 dark:text-amber-400 rounded-sm">
                        <Warning size={9} weight="fill" />{ANOM_LABEL[a] || a}
                      </span>
                    ))}
                    {!(s.anomalies || []).length && <span className="text-[10px] text-muted-foreground">—</span>}
                  </div>
                </td>
                <td className="px-4 py-3 font-mono text-xs">{fmtDur(s.online_seconds)}</td>
                <td className="px-4 py-3 font-mono text-xs"><span className="text-emerald-700 dark:text-emerald-400">{fmtDur(s.active_seconds)}</span> / <span className="text-muted-foreground">{fmtDur(s.idle_seconds)}</span></td>
                <td className="px-4 py-3">{s.active ? <span className="text-xs text-emerald-700 dark:text-emerald-400">online</span> : <span className="text-xs font-mono text-muted-foreground">{(s.end_reason || "ended").replace('_', ' ')}</span>}</td>
              </tr>
            ))}
            {logRows.length === 0 && <tr><td colSpan={8} className="text-center py-8 text-muted-foreground text-sm">No sessions{anomOnly ? " with anomalies" : ""}.</td></tr>}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  );
}
